import { CommonModule } from '@angular/common';
import { Component, HostBinding, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AccessTypeE } from '@generated/be';
import { AppRoutes, hasAccess$ } from '@reza-ais-fe/reza-ais-fe-utils';
import { WrapperComponent } from '@shared/ui/components';
import { AuthService, UIService } from '@shared/utils';
import { cloneDeep, isEqual } from 'lodash-es';
import { MenuItem } from 'primeng/api';
import { MenubarModule } from 'primeng/menubar';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { navigationItems } from './app-navbav.constants';

@Component({
  standalone: true,
  selector: 'reza-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  imports: [WrapperComponent, MenubarModule, CommonModule],
})
export class NavbarComponent {
  @HostBinding('class.is-test')
  get isTestEnvironmentClass() {
    return this._ui.isTestEnv;
  }

  private readonly authService = inject(AuthService);
  private readonly _ui = inject(UIService);
  private readonly router = inject(Router);

  // Získání přístupů pomocí hasAccess$ a kombinace do jednoho observable
  navigation$ = this.getNavigationItems$();

  private getNavigationItems$(): Observable<MenuItem[]> {
    return combineLatest([
      this.authService.user$,
      hasAccess$(AccessTypeE.Opravneni),
      hasAccess$(AccessTypeE.Sablony, AccessTypeE.SablonyVytvareni),
      hasAccess$(AccessTypeE.SablonySkupiny, AccessTypeE.SablonySkupinyVytvareni),
      hasAccess$(AccessTypeE.Certifikaty),
      hasAccess$(AccessTypeE.CertifikatyOvereni),
      hasAccess$(AccessTypeE.Ciselniky, AccessTypeE.CiselnikyEditace),
      hasAccess$(AccessTypeE.OpravneniOvereni),
      hasAccess$(AccessTypeE.StatistikyOkz),
      hasAccess$(AccessTypeE.StatistikySablony),
      hasAccess$(AccessTypeE.StatistikyCertifikaty),
      hasAccess$(AccessTypeE.Reklamace, AccessTypeE.ReklamaceSpravce),
      hasAccess$(AccessTypeE.ReklamaceImpl),
      hasAccess$(AccessTypeE.ArchivaceOkz),
      hasAccess$(AccessTypeE.ArchivaceSablony),
      hasAccess$(AccessTypeE.ArchivaceCertifikaty),
    ]).pipe(
      // filter(([user]) => !!user), // Ensure user is logged in - commented out because navbar is now available in public too
      distinctUntilChanged(isEqual),
      map(
        ([
          user,
          hasOpravneniAccess,
          hasSablonyAccess,
          hasSablonySkupinyAccess,
          hasCertifikatAccess,
          hasCertifikatOvereniAccess,
          hasCiselnikyAccess,
          hasOpravneniOvereniAccess,
          hasStatistikyOkzAccess,
          hasStatistikySablonyAccess,
          hasStatistikyCertifikatyAccess,
          hasReklamaceAccess,
          hasReklamaceImplAccess,
          hasArchivaceOkzAccess,
          hasArchivaceSablonyAccess,
          hasArchivaceCertifikatyAccess,
        ]) =>
          this.filterNavigationItems(
            user,
            hasOpravneniAccess,
            hasSablonyAccess,
            hasSablonySkupinyAccess,
            hasCertifikatAccess,
            hasCertifikatOvereniAccess,
            hasCiselnikyAccess,
            hasOpravneniOvereniAccess,
            hasStatistikyOkzAccess,
            hasStatistikySablonyAccess,
            hasStatistikyCertifikatyAccess,
            hasReklamaceAccess,
            hasReklamaceImplAccess,
            hasArchivaceOkzAccess,
            hasArchivaceSablonyAccess,
            hasArchivaceCertifikatyAccess
          )
      )
    );
  }

  private filterNavigationItems(
    user: boolean,
    hasOpravneniAccess: boolean,
    hasSablonyAccess: boolean,
    hasSablonySkupinyAccess: boolean,
    hasCertifikatAccess: boolean,
    hasCertifikatOvereniAccess: boolean,
    hasCiselnikyAccess: boolean,
    hasOpravneniOvereniAccess: boolean,
    hasStatistikyOkzAccess: boolean,
    hasStatistikySablonyAccess: boolean,
    hasStatistikyCertifikatyAccess: boolean,
    hasReklamaceAccess: boolean,
    hasReklamaceImplAccess: boolean,
    hasArchivaceOkzAccess: boolean,
    hasArchivaceSablonyAccess: boolean,
    hasArchivaceCertifikatyAccess: boolean
  ): MenuItem[] {
    const clonedNavigationItems: MenuItem[] = cloneDeep(navigationItems);

    if (!user) {
      return [];
    }

    const rtn = clonedNavigationItems
      .map((item) => {
        if (item.routerLink === AppRoutes.Opravneni && !hasOpravneniAccess) {
          return null;
        }
        if (item.routerLink === AppRoutes.Certifikaty && !hasCertifikatAccess) {
          return null;
        }
        if (item.routerLink === AppRoutes.CertifikatyOvereni && !hasCertifikatOvereniAccess) {
          return null;
        }

        if (item.id === AppRoutes.OvereniOpravneni && !hasOpravneniOvereniAccess) {
          return null;
        }
        if (
          item.id === AppRoutes.Statistiky &&
          !hasStatistikyOkzAccess &&
          !hasStatistikySablonyAccess &&
          !hasStatistikyCertifikatyAccess
        ) {
          return null;
        }
        if (
          item.id === AppRoutes.Archiv &&
          !hasArchivaceOkzAccess &&
          !hasArchivaceSablonyAccess &&
          !hasArchivaceCertifikatyAccess
        ) {
          return null;
        }

        const newItem = { ...item };

        if (item.id === AppRoutes.Ciselniky) {
          const filteredSubItems = item.items?.filter((subItem) => {
            if (subItem.id === AppRoutes.ObjektyOmezeni && !hasCiselnikyAccess) return false;
            if (subItem.id === AppRoutes.SpecifickaOmezeni && !hasCiselnikyAccess) return false;
            if (subItem.id === AppRoutes.Role && !hasCiselnikyAccess) return false;
            return true;
          });

          if (!filteredSubItems || filteredSubItems.length === 0) return null;

          newItem.items = filteredSubItems;
        }

        if (item.id === AppRoutes.Reklamace) {
          const filteredSubItems = item.items?.filter((subItem) => {
            if (subItem.id === AppRoutes.Reklamace && !hasReklamaceAccess) return false;
            if (subItem.id === AppRoutes.Reklamace_ImplicitnichOpravneni && !hasReklamaceImplAccess) return false;
            if (subItem.id === AppRoutes.Reklamace_KontrolaReklamaciImplicitnichOpravneni && !hasReklamaceImplAccess)
              return false;
            return true;
          });

          if (!filteredSubItems || filteredSubItems.length === 0) return null;

          newItem.items = filteredSubItems;
        }

        if (item.id === AppRoutes.Sablony) {
          const filteredSubItems = item.items?.filter((subItem) => {
            if (subItem.id === AppRoutes.Sablony && !hasSablonyAccess) return false;
            if (subItem.id === AppRoutes.Sablony_Skupiny && !hasSablonySkupinyAccess) return false;
            return true;
          });

          if (!filteredSubItems || filteredSubItems.length === 0) return null;

          newItem.items = filteredSubItems;
        }

        if (
          item.id === AppRoutes.Statistiky &&
          (hasStatistikyCertifikatyAccess || hasStatistikySablonyAccess || hasStatistikyOkzAccess)
        ) {
          const filteredSubItems = item.items?.filter((subItem) => {
            if (subItem.id === AppRoutes.Statistiky_PocetOpravneniKZastupovani && !hasStatistikyOkzAccess) {
              return false;
            }
            if (subItem.id === AppRoutes.Statistiky_ZmenyOpravneniKZastupovani && !hasStatistikyOkzAccess) {
              return false;
            }
            if (subItem.id === AppRoutes.Statistiky_OpravneniKZastupovaniDleSablon && !hasStatistikySablonyAccess) {
              return false;
            }
            if (subItem.id === AppRoutes.Statistiky_PoctuCertifikatu && !hasStatistikyCertifikatyAccess) {
              return false;
            }
            return true;
          });

          if (!filteredSubItems || filteredSubItems.length === 0) {
            return null;
          }

          newItem.items = filteredSubItems;
        }

        if (
          item.id === AppRoutes.Archiv &&
          (hasArchivaceOkzAccess || hasArchivaceSablonyAccess || hasArchivaceCertifikatyAccess)
        ) {
          const filteredSubItems = item.items?.filter((subItem) => {
            if (subItem.id === AppRoutes.Opravneni && !hasArchivaceOkzAccess) {
              return false;
            }
            if (subItem.id === AppRoutes.Sablony && !hasArchivaceSablonyAccess) {
              return false;
            }
            if (subItem.id === AppRoutes.Certifikaty && !hasArchivaceCertifikatyAccess) {
              return false;
            }
            return true;
          });

          if (!filteredSubItems || filteredSubItems.length === 0) {
            return null;
          }

          newItem.items = filteredSubItems;
        }

        return newItem;
      })
      .filter((item) => item !== null) as MenuItem[];
    return rtn;
  }
}
